// AIDialSelectedModal — "AI Dial Selected" quick-launch flow used by the
// Leads bulk action bar and the Sales Pipeline column menu.
//
// Picks a bot, takes optional notes, and launches a quick campaign via
// POST /api/ai-dialer/campaigns/quick. On success, shows a toast linking back
// to the AI Dialer page so the agent can watch progress.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Bot, Loader2, Sparkles, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost } from '../lib/api.js';

export default function AIDialSelectedModal({
  open,
  onClose,
  leadIds = [],
  contextLabel = '',     // e.g. "Quick Dial" or "Stage: Hot"
  onLaunched,            // called with the created campaign
}) {
  const nav = useNavigate();
  const [botId, setBotId] = useState('');
  const [notes, setNotes] = useState('');
  const [pace, setPace] = useState(30);

  const { data: botsData, isLoading: botsLoading } = useQuery({
    queryKey: ['bots'],
    queryFn:  () => apiGet('/bots'),
    enabled:  open,
  });
  const bots = botsData?.data || [];

  const launchMut = useMutation({
    mutationFn: (payload) => apiPost('/ai-dialer/campaigns/quick', payload),
    onSuccess: (r) => {
      const c = r?.data?.campaign;
      const name = c?.name || 'AI campaign';
      toast.success(
        `${name} launched with ${leadIds.length} lead${leadIds.length !== 1 ? 's' : ''}. View in AI Dialer.`
      );
      onLaunched?.(c);
      onClose?.();
      setNotes('');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to launch'),
  });

  // Escape key closes (a11y) — only when open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const defaultName = `${contextLabel || 'Quick Dial'} — ${today}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="panel w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/40 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-civix-purple/20 flex items-center justify-center">
            <Bot className="w-5 h-5 text-civix-purple" />
          </div>
          <div>
            <div className="font-semibold text-white">AI Dial Selected</div>
            <div className="text-xs text-white/40">
              Launch an AI campaign for {leadIds.length} lead{leadIds.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="form-label">Which bot?</label>
            {botsLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-white/40" />
            ) : bots.length === 0 ? (
              <div className="text-xs text-yellow-400 bg-yellow-400/5 p-3 rounded-lg">
                You don't have any bots yet.{' '}
                <button
                  onClick={() => { onClose?.(); nav('/ai-dialer'); }}
                  className="underline"
                >
                  Create one in AI Dialer
                </button>
                .
              </div>
            ) : (
              <select
                value={botId}
                onChange={(e) => setBotId(e.target.value)}
                className="input w-full"
              >
                <option value="">— Default voice + script —</option>
                {bots.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.name} {b.status !== 'ready' ? `(${b.status})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="form-label">Pace (calls/hour)</label>
            <input
              type="number"
              min={1}
              max={500}
              value={pace}
              onChange={(e) => setPace(Math.max(1, Number(e.target.value) || 30))}
              className="input w-full"
            />
          </div>

          <div>
            <label className="form-label">Notes for the bot (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="e.g. These leads came from the May Facebook spend. Focus on term life options."
              className="input w-full"
            />
          </div>

          <div className="text-xs text-white/40 bg-white/5 rounded-lg p-3">
            TCPA, DNC, and state quiet-hour rules are enforced automatically before each call.
          </div>
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button
            onClick={() => launchMut.mutate({
              botId:        botId || undefined,
              leadIds,
              name:         defaultName,
              notes,
              pacePerHour:  pace,
              launchNow:    true,
            })}
            disabled={launchMut.isPending || leadIds.length === 0 || bots.length === 0}
            className="btn-primary flex items-center gap-2"
          >
            {launchMut.isPending
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Sparkles className="w-4 h-4" />}
            Launch Campaign
          </button>
        </div>
      </div>
    </div>
  );
}
